import * as React from 'react'
import {useNavigate} from 'react-router-dom'
import {Box, CircularProgress, Typography} from '@mui/material'
import {useSnackbar} from 'notistack'
import withLayout from '../../../hocs/Layout'
import {getMerchant} from '../../../helpers/ReduxHelpers'
import PageTitle from '../../../common/PageTitle'
import {pageContainerSx} from '../../../styles/patterns'
import {TABS, NOW} from './data/reference'
import {unpinClock, pinClock} from './data/clock'
import {fmtKg} from './format'
import {fetchDisputePage, fetchAllDisputes, fetchSummary, fetchSkuAnalytics, fetchWeekly, fetchCourierAnalytics, apiAccept, apiDispute, apiAcceptBulk, apiSubmitEvidence, apiSkuShipmentCounts, apiSkuHistory, isInternalLogin} from './data/api'
import {mapSkuRow} from './data/listBody'
import {singleMerchantOf} from './data/scope'
import {useDisputeState} from './state/useDisputeState'
import {queryRows, queryTotals, paginate} from './state/query'
import KpiStrip from './KpiStrip'
import TabBar from './TabBar'
import FilterBar, {sortsForTab, DEFAULT_SORT} from './FilterBar'
import DisputeGrid from './DisputeGrid'
import DisputeDrawer from './DisputeDrawer'
import {useDisputeActions, acceptableRows, disputeBlockedReason} from './actions'
import EvidenceStudio, {applyEvidenceToRows} from './EvidenceStudio'
import {applyEvidenceIntake} from './state/evidenceIntake'
import SkuIntelligence from './SkuIntelligence'
import TriageView from './TriageView'
import RecoveryAnalytics from './RecoveryAnalytics'

/** Where weight freezes are raised (the Merchant Weight Freeze module). */
const MERCHANT_FREEZE_PATH = '/weight-freeze-v2/initiate'

// The page renders at 80% of its natural size.
//
// The request was literally "at 100% browser zoom this page should look the way
// it does at 80%", and that is what `zoom` expresses: it scales type, padding,
// borders and column widths together, in one place. Getting the same result by
// editing individual values was not available — the page's own type is already
// at the panel's floor (12px dominant, 11px for sub-detail), so a further 20%
// off would mean sub-10px body text, and it still would not have shrunk the
// spacing or the fixed column widths that decide whether the table fits.
//
// Deliberately NOT applied to the sidebar or topbar, which are outside this
// component and stay at full size.
//
// `zoom` is honoured by Chrome, Edge, Safari and Firefox 126+. It composes with
// browser zoom rather than fighting it, so a user who zooms to 150% still gets
// 150% of this — accessibility zoom keeps working.
//
// Overlays (drawer, dialogs, popovers) render in portals attached to
// document.body, OUTSIDE this container, so they are not scaled. That is left
// deliberate: an overlay is a focused, one-at-a-time task where full-size text
// is welcome, and it is the page density that was the complaint.
const PAGE_ZOOM = 0.8

const PER_PAGE = 10

/**
 * Why a shipment can be missing from the SKU table.
 *
 * A weight discrepancy only carries a SKU when its shipment held exactly ONE distinct product —
 * WeightDiscrepencyCalculator.denormaliseSkuInfo leaves it unset otherwise, because a parcel holding
 * three products has no single product to attribute the extra weight to. On production that is about
 * 29% of orders, so it is a permanent share rather than a backlog, and the server now excludes those
 * rows instead of collapsing them into one unnamed row that would top the table forever.
 *
 * They are still counted everywhere else in the module — the ledger, the tab counts and the KPI
 * strip all include them. Only this table, whose whole question is "which product should I fix",
 * leaves them out.
 */
const MULTI_SKU_NOTICE =
  'Shipments containing more than one product are not listed or counted here — a parcel with several '
  + 'products cannot be attributed to one of them. They still appear in the ledger and in the totals above.'

// Tabs backed by the results grid built in Task 6. SKU Weight Intelligence
// and Recovery Analytics (Task 9) are entirely different views — a SKU
// roll-up and a reporting dashboard, not a status-filtered dispute list —
// and are rendered separately below.
const GRID_TABS = new Set(['action', 'dispute', 'resolved', 'auto'])

/**
 * The account the export filename is stamped with. getMerchant() reads the
 * authenticated session straight out of the redux store, which the dev
 * harness (weight/__harness__/WeightHarness.js) renders this page without —
 * hence the guard rather than an optional chain: with no session the read
 * throws before it can return undefined.
 */
function accountName() {
  try {
    return (getMerchant() || {}).name || 'Merchant'
  } catch {
    return 'Merchant'
  }
}

/**
 * `initialRows` decides which of the module's two modes this mount runs in:
 *
 * SEEDED (initialRows given — the dev harness passes the frozen demo
 * dataset): the pre-integration behaviour, exactly. Every action computes
 * locally via applyTransition/applyEvidenceIntake, nothing touches a network,
 * a reload discards everything.
 *
 * LIVE (no initialRows — the real /weight-disputes-v2 route): rows are
 * fetched once from the weight-v2 backend (data/api.js, newest first, all
 * pages) and every action/evidence submit goes through the API; local state
 * is updated only with what the server returned. Filtering, sorting, tabs,
 * KPIs and analytics all still run client-side over the fetched set — the
 * deliberate transitional strategy documented in data/api.js.
 */
function WeightDisputesV2({initialRows = null}) {
  const live = !initialRows
  // The module clock follows the mode (data/clock.js): the wall clock on a live
  // mount, the dataset's pinned instant when seeded. Set synchronously, before
  // anything below computes a deadline or a range against it.
  if (live) unpinClock(); else pinClock(NOW)
  const {enqueueSnackbar} = useSnackbar()
  const {
    tab, setTab, filters, setFilter, applyFilters, resetFilters, range, setRange, sort, setSort,
    page, setPage, selection, toggleSelect, clearSelection, selectOnly,
  } = useDisputeState({initialRange: live ? '90d' : 'all'})

  // A status filter belongs to the tab it was picked on; on another tab it would match nothing.
  React.useEffect(() => {
    const def = TABS.find((t) => t.id === tab)
    if (filters.status && !(def && def.statuses && def.statuses.includes(filters.status))) setFilter('status', '')
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  // The module's single mutable row set. Seeded mounts start from the rows
  // they were handed and never write anywhere else; live mounts start empty
  // and are filled by the fetch effect below. Either way, every later action
  // only ever updates this React state, so the grid and drawer reflect it
  // immediately.
  const [disputes, setDisputes] = React.useState(() => initialRows || [])
  const [loading, setLoading] = React.useState(live)
  const [openRowId, setOpenRowId] = React.useState(null)
  const [evidenceStudioOpen, setEvidenceStudioOpen] = React.useState(false)

  // Page size is state now that the footer exposes a "Rows per page" control.
  // PER_PAGE stays the initial value. Changing the size sends the reader back
  // to page 1: keeping the page number would silently jump them somewhere else
  // in the list (page 5 of 10-row pages is row 41; page 5 of 50-row pages is
  // row 201), and can land past the end of the data entirely.
  //
  // Declared up here rather than beside the pagination below because the live fetch reads it: on a
  // live mount the page size is what the SERVER is asked for, not a slice taken afterwards.
  const [perPage, setPerPage] = React.useState(PER_PAGE)

  const handlePerPageChange = React.useCallback((next) => {
    setPerPage(next)
    setPage(1)
  }, [setPage])

  // ---- live data ---------------------------------------------------------------------------
  //
  // The server answers the page, the tab counts and the KPI totals; the panel holds one page.
  //
  // This used to be fetchAllDisputes(): up to 25 requests of 200 rows, then every figure on screen
  // filtered, sorted, counted and aggregated in the browser. That is correct for a few hundred
  // discrepancies. The largest merchant in production has 1,866,768 — the panel read 5,000 of them
  // (0.3%) and presented the totals as the merchant's whole book, with nothing on screen
  // contradicting anything, because every number came from the same truncated set.
  //
  // SEEDED mounts are untouched and still derive everything from initialRows, which is what the
  // module's tests exercise.
  const [serverPage, setServerPage] = React.useState(null)   // {rows, count, remaining, tabCounts}
  const [serverTotals, setServerTotals] = React.useState(null)
  const [reloadKey, setReloadKey] = React.useState(0)
  /** Re-read the current page — after an action, whose result may move a row to another tab. */
  const reload = React.useCallback(() => setReloadKey((n) => n + 1), [])

  React.useEffect(() => {
    if (!live) return undefined
    let cancelled = false
    setLoading(true)
    const params = {tab, filters, range, sort, page, size: perPage}
    fetchDisputePage(params)
      .then((res) => {
        if (cancelled) return
        setServerPage(res)
        setDisputes(res.rows)   // the drawer and the action handlers read rows from here
      })
      .catch((err) => {
        if (!cancelled) {
          enqueueSnackbar(
            `Could not load weight discrepancies: ${(err && err.message) || 'network error'}`,
            {variant: 'error'},
          )
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, tab, filters, range, sort, page, perPage, reloadKey, enqueueSnackbar])

  // The two analytics tabs describe the whole filtered set as well, and only the server can. Fetched
  // when their tab is open rather than on every load, because each is an aggregation over the
  // merchant's book and nobody should pay for a tab they are not looking at.
  const [serverSkus, setServerSkus] = React.useState(null)
  // How many SKUs the SKU tab lists. Its label borrowed the whole discrepancy count, which also takes in
  // multi-product shipments that are never attributed to a SKU, so "(8)" opened onto four rows.
  const [serverSkuTotal, setServerSkuTotal] = React.useState(null)
  React.useEffect(() => { setServerSkuTotal(null) }, [filters, range])
  const [serverWeeks, setServerWeeks] = React.useState(null)
  const [serverCouriers, setServerCouriers] = React.useState(null)

  React.useEffect(() => {
    if (!live || tab !== 'sku') return undefined
    let cancelled = false
    fetchSkuAnalytics({tab, filters, range, sort, page: 1, size: 100})
      .then((res) => {
        if (cancelled) return
        const skuRows = (res.rows || []).map(mapSkuRow).filter(Boolean)
        setServerSkus(skuRows)
        setServerSkuTotal(skuRows.length + (res.remaining || 0))
      })
      .catch((err) => {
        if (!cancelled) enqueueSnackbar(`Could not load SKU intelligence: ${(err && err.message) || 'network error'}`, {variant: 'error'})
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, tab, filters, range, reloadKey])

  React.useEffect(() => {
    if (!live || tab !== 'analytics') return undefined
    let cancelled = false
    Promise.all([fetchWeekly({tab, filters, range}), fetchCourierAnalytics({tab, filters, range})])
      .then(([w, c]) => { if (cancelled) return; setServerWeeks(w || []); setServerCouriers(c || []) })
      .catch((err) => {
        if (!cancelled) enqueueSnackbar(`Could not load recovery analytics: ${(err && err.message) || 'network error'}`, {variant: 'error'})
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, tab, filters, range, reloadKey])

  // The KPI strip describes the whole filtered set, not the page — so it comes from the server too.
  // A failure here leaves the strip on its previous figures rather than blanking the screen.
  React.useEffect(() => {
    if (!live) return undefined
    let cancelled = false
    fetchSummary({tab, filters, range, sort})
      .then((t) => { if (!cancelled && t) setServerTotals(t) })
      .catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, range, reloadKey])

  // Real per-SKU shipment volumes for the SKU Weight Intelligence tab (D17). Live
  // only; seeded mounts keep the deterministic placeholder. A failure here just
  // leaves the placeholder in place — the tab must not go blank over a count.
  const [shipmentCounts, setShipmentCounts] = React.useState(null)
  React.useEffect(() => {
    if (!live || loading || !disputes.length) return undefined
    let cancelled = false
    const skus = Array.from(new Set(disputes.map((r) => r.sku).filter(Boolean)))
    // A merchant login needs no merchantId; a Prozo-admin login sees many merchants and the
    // server refuses SKU reads without one — pass it only when every row shares one.
    apiSkuShipmentCounts(skus, singleMerchantOf(disputes))
      .then((counts) => { if (!cancelled) setShipmentCounts(counts) })
      .catch(() => { /* placeholder stays */ })
    return () => { cancelled = true }
  }, [live, loading, disputes])

  // DisputeDrawer's own focus-fallback target (see that file's doc comment
  // and TabBar.js's forwarded focusActive()) — the same focusFallbackRef
  // mechanism the Freeze module's rail-based sections already use, applied
  // here to the tab bar since this module has no rail.
  const tabBarRef = React.useRef(null)

  // 'ship' (ledger, one row per shipment) | 'product' (the same rows
  // grouped by SKU — see the FilterBar's View control). Bridge-local UI
  // state, not part of useDisputeState: it never affects which rows match,
  // only how the already-matched set is displayed, so it has no business
  // near the selection/pagination contract that hook owns.
  const [view, setView] = React.useState('ship')

  // Every jump that changes `tab` also resets the view to the ledger — a
  // merchant who jumps in from the KPI strip or SKU Weight Intelligence
  // expects the plain list, not whatever grouping was left on from a
  // previous visit to Action Required.
  const changeTab = React.useCallback((next) => {
    setTab(next)
    setView('ship')
    // A tab can invalidate the active sort: the "Deadline" order exists
    // only on Action Required, because auto-accept can only still happen there.
    // Left alone, the dropdown would render blank on the destination tab while
    // the rows stayed in an order nothing on screen names.
    if (!sortsForTab(next).some((s) => s.id === sort)) setSort(DEFAULT_SORT)
  }, [setTab, sort, setSort])

  const navigate = useNavigate()

  // The live-backend executor handed to useDisputeActions — see that hook's
  // `execute` contract. Each branch resolves to the server's updated row(s)
  // or throws a toast-ready Error. Absent (seeded mounts), the hook keeps its
  // original local-only applyTransition path.
  const liveExecute = React.useCallback(async (actionType, subject, opts) => {
    switch (actionType) {
      case 'accept':
        return apiAccept(subject, opts && opts.remark)
      case 'acceptMany':
        return apiAcceptBulk(subject, opts && opts.remark)
      case 'dispute': {
        // Same client-side gate applyTransition enforces locally — the server
        // re-checks it (422) but the merchant should hit the readable sentence
        // here, not a round-trip later.
        const blocked = disputeBlockedReason(subject)
        if (blocked) throw new Error(`AWB ${subject.awb} cannot be disputed yet. ${blocked}`)
        return apiDispute(subject, opts && opts.remark)
      }
      case 'undoAccept':
        // No reversal endpoint exists yet — undo needs the money-side decisions
        // (what a reversal does to an already-settled charge) before it can be
        // real. Refusing beats pretending.
        throw new Error('Undo is not available yet — reversing a decision is pending business sign-off.')
      case 'freezeSku':
        // Freezes are raised, with the merchant's own packing and evidence, in Merchant Weight Freeze:
        // take the merchant there rather than refuse. The dispute itself is unchanged.
        navigate(MERCHANT_FREEZE_PATH)
        return subject
      default:
        throw new Error(`Unknown action: ${actionType}`)
    }
  }, [navigate])

  const internalLogin = React.useMemo(() => live && isInternalLogin(), [live])
  const disputeActions = useDisputeActions({
    onChange: (updatedRow) => {
      // Patch the row in place first, so the drawer reflects the new status immediately...
      setDisputes((prev) => prev.map((r) => (r.id === updatedRow.id ? updatedRow : r)))
      // ...then re-read the page, because on a live mount the ROW HAS USUALLY LEFT THIS TAB. Accepting
      // or disputing moves it from Action Required into Resolved or In Dispute, and with the server
      // paging there is no longer a full set in the browser to re-filter: the tab counts, the page
      // and the KPI totals all have to come back from the server or they go stale on the spot.
      if (live) reload()
    },
    execute: live ? liveExecute : undefined,
    // A Prozo login disputes and accepts on the merchant's behalf, which the server refuses without a remark.
    onBehalf: internalLogin,
  })

  // The KPI strip is a dataset-wide overview, not scoped to the active tab —
  // it needs to answer "how much is at risk / recovered right now". The date
  // range is the one control that should move both it and the list.
  const overviewRows = React.useMemo(
    () => queryRows(disputes, {tab: null, filters: {}, range}),
    [disputes, range],
  )
  // LIVE: the strip describes every row under the filters, which only the server can total.
  // SEEDED: derived from the dataset in hand, as before.
  const totals = React.useMemo(
    () => (live && serverTotals ? serverTotals : queryTotals(overviewRows)),
    [live, serverTotals, overviewRows],
  )

  // Tab counts reflect the same filters the grid does, so a tab reading 12
  // opens onto 12 rows.
  // LIVE: the server returns tabCounts with every page, so a tab label counts the merchant's whole
  // book under the current filters rather than the rows that happen to be loaded. The two view-only
  // tabs (SKU, analytics) describe everything, so they carry the all-count.
  // SEEDED: counted from the dataset in hand, as before.
  const counts = React.useMemo(() => {
    if (live) {
      const tc = (serverPage && serverPage.tabCounts) || {}
      const c = {}
      TABS.forEach((t) => { c[t.id] = (t.statuses ? tc[t.id] : tc.all) || 0 })
      // The SKU tab counts SKUs once it has loaded them, and shows no number before that.
      c.sku = typeof serverSkuTotal === 'number' ? serverSkuTotal : undefined
      return c
    }
    const c = {}
    TABS.forEach((t) => {
      c[t.id] = queryRows(disputes, {tab: t.id, filters, range, sort}).length
    })
    return c
  }, [live, serverPage, serverSkuTotal, disputes, filters, range, sort])

  // LIVE: the server already applied the tab, the filters, the range and the sort, so re-running
  // queryRows here would filter the page a second time against a clock the server does not share —
  // the urgency bands in particular are computed from "now" on each side.
  // SEEDED: filtered and sorted in the browser, as before.
  const rows = React.useMemo(
    () => (live ? (serverPage ? serverPage.rows : []) : queryRows(disputes, {tab, filters, range, sort})),
    [live, serverPage, disputes, tab, filters, range, sort],
  )

  // "Tab genuinely has no rows" ignores every filter and the date range —
  // it asks whether this status group ever has anything in it at all, which
  // is exactly the condition "Clear filters" cannot fix (clearing filters
  // wouldn't change a tab that's empty on its own). Anything else that
  // yields zero rows is the current filters/range being too narrow.
  //
  // LIVE: `disputes` is now one page, so asking it this question would answer "the tab is empty"
  // every time a filter merely matched nothing — and offer no way out, because the empty state that
  // claims a tab is inherently empty deliberately hides "Clear filters". The server's tab counts
  // cannot settle it either: they are already scoped to the active filters and date window. So the
  // tab counts as inherently empty only when nothing is narrowing the view at all.
  const isTabEmpty = React.useMemo(() => {
    if (!live) return queryRows(disputes, {tab, filters: {}, range: 'all', sort}).length === 0
    const narrowed = Object.values(filters).some(Boolean) || (range && range !== 'all')
    return !narrowed && ((serverPage && serverPage.count) || 0) === 0
  }, [live, serverPage, disputes, tab, filters, range, sort])

  // The row currently open in the drawer — re-derived from `disputes` on
  // every render so an action taken inside the drawer (e.g. Dispute) is
  // reflected the instant it lands in state, without a second selection step.
  const openRow = React.useMemo(
    () => disputes.find((r) => r.id === openRowId) || null,
    [disputes, openRowId],
  )

  // LIVE: the rows in hand ARE the page; `total` is the server's count, so the footer reads
  // "1-10 of 1,866,768" rather than "1-10 of however many we fetched".
  const pageData = React.useMemo(() => {
    if (!live) return paginate(rows, page, perPage)
    const total = (serverPage && serverPage.count) || 0
    return {items: rows, total, pages: Math.max(1, Math.ceil(total / perPage))}
  }, [live, serverPage, rows, page, perPage])
  const matchingIds = React.useMemo(() => rows.map((r) => r.id), [rows])

  // Selection is never cleared on a filter/range/sort change (only on tab
  // switch — see useDisputeState) because clearing it on every keystroke of
  // the search box would make selection unusable. Instead, the *effective*
  // selection shown to the grid (banner count, "select all matching" gate,
  // header/row checkboxes) is always intersected with the rows currently
  // matching the filters, so a row the user can no longer see can never be
  // silently counted as selected.
  const effectiveSelection = React.useMemo(() => {
    const matchingSet = new Set(matchingIds)
    const next = new Set()
    selection.forEach((id) => { if (matchingSet.has(id)) next.add(id) })
    return next
  }, [selection, matchingIds])

  // The ONLY id list the Evidence Studio (Task 8) is ever handed. Derived
  // from `effectiveSelection`, never the raw `selection` — see that memo's
  // comment and applyEvidenceToRows's safety contract in EvidenceStudio.js.
  // Using the raw selection here would let a bulk evidence upload apply to,
  // and report success for, rows the merchant filtered away and never saw.
  const evidenceStudioIds = React.useMemo(
    () => Array.from(effectiveSelection),
    [effectiveSelection],
  )

  const handleSelectAll = React.useCallback((ids, checked) => {
    ids.forEach((id) => {
      const has = selection.has(id)
      if (checked && !has) toggleSelect(id)
      else if (!checked && has) toggleSelect(id)
    })
  }, [selection, toggleSelect])

  const account = React.useMemo(() => accountName(), [])

  // Which of the selected rows accepting is actually valid for. Derived from
  // `disputes` (the full set) rather than the page slice, because "Select all
  // N matching" can select rows that are not on screen; and through
  // acceptableRows(), so the bulk control and the per-row Accept button are
  // answering the same question with the same code.
  const acceptableSelected = React.useMemo(
    () => acceptableRows(disputes.filter((r) => effectiveSelection.has(r.id))),
    [disputes, effectiveSelection],
  )

  // One confirm for the whole set, then the ordinary per-row transition
  // inside useDisputeActions. The selection is cleared only once the rows
  // have actually changed — cancelling the dialog leaves it exactly as it
  // was, so the merchant does not have to re-select to try again.
  const handleAcceptSelected = React.useCallback(async () => {
    if (!acceptableSelected.length) return
    const accepted = await disputeActions.acceptMany(acceptableSelected)
    if (accepted && accepted.length) clearSelection()
  }, [acceptableSelected, disputeActions, clearSelection])

  const handleOpenRow = (row) => setOpenRowId(row.id)
  const handleCloseDrawer = () => setOpenRowId(null)

  // The drawer opens only on a click. A dispute moves its row out of this tab, so the drawer vanishes
  // with it while openRowId stayed set — and the next tab that listed that row opened the drawer
  // again by itself. Forget the row once it leaves the list, and on every tab change.
  React.useEffect(() => { setOpenRowId(null) }, [tab])
  React.useEffect(() => {
    if (openRowId && !openRow) setOpenRowId(null)
  }, [openRowId, openRow])
  const handleAct = (actionType) => {
    if (!openRow) return
    disputeActions[actionType](openRow)
  }

  // Row-level actions fire against the row the button belongs to, not against
  // whatever the drawer last had open — handleAct above closes over `openRow`,
  // which is null when nothing is open and stale otherwise.
  const handleRowAct = React.useCallback((actionType, row) => {
    if (!row || !disputeActions[actionType]) return
    disputeActions[actionType](row)
  }, [disputeActions])

  // Every KPI card opens the set it counts. changeTab (not setTab) is used
  // throughout so each jump also resets the view to the ledger — a merchant
  // arriving from the strip wants the plain list, not whatever grouping was
  // left on from an earlier visit.
  //
  // resetFilters on the three plain jumps matters: without it a card reading 34
  // could land on a tab showing 3, because a courier or search filter from
  // before was still applied. The count on the card and the rows on screen have
  // to agree, or the card has lied.
  const handleRiskClick = () => {
    resetFilters()
    changeTab('action')
  }

  const handleExpiringClick = () => {
    resetFilters()
    changeTab('action')
    setFilter('urgency', 'expiring48')
  }

  const handleDisputedClick = () => {
    resetFilters()
    changeTab('dispute')
  }

  const handleResolvedClick = () => {
    resetFilters()
    changeTab('resolved')
  }

  // Avg resolution is a duration, not a set of rows, so there is no tab that
  // "holds" it — Recovery Analytics is where the trend behind it lives.
  const handleAvgClick = () => changeTab('analytics')

  // SKU Weight Intelligence's click-through — see SkuIntelligence.js. Jumps
  // to Action Required and narrows the search to this SKU, the same
  // tab-then-filter pattern as handleExpiringClick above. Also doubles as
  // the "View disputes" handler from the filter bar's own "By product"
  // view (see the FilterBar render block below): reusing changeTab means
  // that click lands on the ledger view narrowed to this SKU rather than
  // staying in the grouping it was clicked from.
  // The SKU table's one action is now "Freeze SKU", whose drawer ends in a
  // request rather than a jump back to the ledger — so `handleOpenSku`, which
  // narrowed Action Required to the SKU, no longer has a caller.
  //
  // Session-only, like every other mutation in this module: nothing here
  // reaches a backend, so the request is announced and not persisted. Wiring it
  // to the Weight Freeze module would mean crossing a module boundary the two
  // are deliberately kept either side of.
  const handleRequestFreeze = (sku, weight) => {
    if (live) {
      // No freeze-request endpoint exists yet (freeze lifecycle pending
      // business sign-off) — a success toast here would be a false claim.
      navigate(MERCHANT_FREEZE_PATH)
      return
    }
    enqueueSnackbar(`Weight freeze requested for ${sku} at ${fmtKg(weight)} — Prozo will review it.`, {variant: 'success'})
  }

  const handleCloseEvidenceStudio = () => setEvidenceStudioOpen(false)

  // The triage view's per-group verdict.
  //
  // Accept loops: disputeActions.accept carries its own confirm and toast per
  // row, and conceding is genuinely a per-shipment decision.
  //
  // Dispute deliberately does NOT loop. One packing video covers the whole
  // group — that is the entire argument for grouping by cause — so it selects
  // the group's open rows and opens the Evidence Studio once over the set,
  // This is now the ONLY way into the Evidence Studio over a set of rows: the
  // ledger's own "Add evidence to N selected" button was removed from the
  // selection bar, which left bulk accept as the ledger's only bulk verdict and
  // grouping-by-cause as the place a bulk dispute is argued from.
  // selectOnly (not clearSelection + toggles) so the studio receives exactly
  // this group and nothing left over from an earlier selection.
  const handleGroupAct = React.useCallback((actionType, members) => {
    const list = members || []
    if (!list.length) return
    if (actionType === 'dispute') {
      // The row drawer, not the full-page studio: a merchant clicking Dispute
      // on a card expects the same surface the ledger gives them, and the
      // studio replaced the whole page for what reads as a per-shipment action.
      // The group is still selected, so whatever the drawer is extended to do
      // across a selection has the set to hand.
      selectOnly(list.map((r) => r.id))
      handleOpenRow(list[0])
      return
    }
    list.forEach((row) => handleRowAct(actionType, row))
  }, [handleRowAct, selectOnly])

  // Evidence Studio mutation — the same local-only pattern as disputeActions
  // above: applyEvidenceToRows (EvidenceStudio.js) returns a new disputes
  // array with only the ids it was given changed. Nothing here writes to
  // data/noberoDisputes.js or a backend; a page reload discards it.
  const handleSubmitEvidence = (recordIds, pending) => {
    setDisputes((prev) => applyEvidenceToRows(prev, recordIds, pending))
  }

  // DisputeDrawer's own single-record evidence flow (EvidenceIntake.js,
  // Phase C2-C5). Seeded: the local-only mutation pattern, via
  // state/evidenceIntake.js's applyEvidenceIntake. Live: the files and
  // figures go to the evidence endpoint and the row is replaced with what
  // the server returned — resolving to `false` on failure, which tells
  // EvidenceIntake to keep its form state for a retry (it toasts success
  // itself only when this resolves truthy).
  const handleSubmitRowEvidence = (rowId, payload) => {
    if (!live) {
      setDisputes((prev) => prev.map((r) => (r.id === rowId ? applyEvidenceIntake(r, payload) : r)))
      return true
    }
    return apiSubmitEvidence(rowId, payload)
      .then((updated) => {
        if (updated) setDisputes((prev) => prev.map((r) => (r.id === rowId ? updated : r)))
        return true
      })
      .catch((err) => {
        enqueueSnackbar((err && err.message) || 'Evidence upload failed', {variant: 'error'})
        return false
      })
  }

  return (
    <Box sx={{...pageContainerSx, zoom: PAGE_ZOOM}}>
      {/* Kept to one line. The old copy ran 103 characters and wrapped after
          "recover"; this is 67, which leaves headroom on narrower windows
          rather than only just fitting on a wide one. The dropped clause was
          "and recover what you were overcharged" — the least load-bearing of
          the three, now that the module reports counts rather than money
          almost everywhere. */}
      <PageTitle subTitle='Review courier weight discrepancies and dispute them with evidence.'>
        Weight Discrepancy
      </PageTitle>

      {/* The initial live fetch replaces the page body rather than sitting
          beside it: rendering the tabs/grid over an empty array would flash
          "nothing here" states that are not yet true. */}
      {loading ? (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 10}}>
          <CircularProgress size={22} />
          <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
            Loading weight discrepancies…
          </Typography>
        </Box>
      ) : (
      <>
      {/* Dataset-wide overview (range-scoped, not tab-scoped — see the
          overviewRows comment above), so it stays meaningful and keeps
          reconciling with SKU Weight Intelligence / Recovery Analytics
          below it on every tab, not just the results-grid ones. */}
      <KpiStrip
        totals={totals}
        onRiskClick={handleRiskClick}
        onExpiringClick={handleExpiringClick}
        onDisputedClick={handleDisputedClick}
        onResolvedClick={handleResolvedClick}
        onAvgClick={handleAvgClick}
      />

      <TabBar ref={tabBarRef} tabs={TABS} active={tab} counts={counts} onChange={changeTab} />

      {/* FilterBar's fields narrow a list of individual disputes down to a
          workable page — SKU Weight Intelligence is already a roll-up with
          nothing to page through, and Recovery Analytics is a fixed pair of
          tables/charts, so neither has a use for it. The "View" segmented
          control (`view`/`onViewChange`) is wired for the Action Required
          tab only, mirroring the offline mockup's own scoping ("backlog" =
          the open queue) — FilterBar renders its filters row regardless, but
          only shows the view bar when these are passed.

          `rows` is handed over for the Download button, and it is the FULL
          filtered set — every page, this tab, current filters and date range
          — not `pageData.items`. The export follows the filters, so the file
          and the count in the pagination footer always agree. */}
      {GRID_TABS.has(tab) && (
        <FilterBar
          tab={tab}
          filters={filters}
          onChange={setFilter}
          onApply={applyFilters}
          onReset={resetFilters}
          sort={sort}
          onSortChange={setSort}
          view={tab === 'action' ? view : null}
          onViewChange={tab === 'action' ? setView : null}
          range={range}
          onRangeChange={setRange}
          rows={rows}
          account={account}
          fetchAllRows={live ? () => fetchAllDisputes({tab, filters, range, sort}) : undefined}
        />
      )}

      {/* "By product" is the triage view: the same rows the ledger shows,
          bucketed by cause so one verdict settles a whole pattern.
          It runs on `rows` — the SAME tab/filter-scoped set DisputeGrid is
          paginated from below, never a separate query — so switching the View
          control can never disagree with the ledger about what is in queue.

          This used to mount SkuIntelligence, i.e. the SKU tab's
          "Recommended weight corrections" table. That was the wrong surface:
          the two answer different questions. SkuIntelligence asks which
          declared weights are wrong and should be corrected at source; this
          view asks which of the shipments in front of you to accept or
          dispute right now. SkuIntelligence still owns the SKU tab below. */}
      {tab === 'action' && view === 'product' && (
        <TriageView
          rows={rows}
          sort={sort}
          onOpenRow={handleOpenRow}
          onGroupAct={handleGroupAct}
          onClearFilters={resetFilters}
        />
      )}

      {GRID_TABS.has(tab) && !(tab === 'action' && view === 'product') && (
        <DisputeGrid
          rows={pageData.items}
          page={Math.min(page, pageData.pages)}
          perPage={perPage}
          total={pageData.total}
          onPageChange={setPage}
          onPerPageChange={handlePerPageChange}
          selection={effectiveSelection}
          onToggleSelect={toggleSelect}
          onSelectAll={handleSelectAll}
          onClearSelection={clearSelection}
          onOpenRow={handleOpenRow}
          isTabEmpty={isTabEmpty}
          onClearFilters={resetFilters}
          tab={tab}
          onAct={handleRowAct}
          acceptableCount={acceptableSelected.length}
          onAcceptSelected={handleAcceptSelected}
        />
      )}

      {/* Both fed from overviewRows — the same range-filtered, tab/status/
          filter-agnostic row set the KPI strip's `totals` is built from
          above — so switching the date range moves the KPI cards and
          these two tabs together and they can never disagree. */}
      {tab === 'sku' && (
        <SkuIntelligence
          rows={overviewRows}
          skus={live ? serverSkus : null}
          notice={live ? MULTI_SKU_NOTICE : null}
          onRequestFreeze={handleRequestFreeze}
          onOpenFreezeModule={live ? () => navigate(MERCHANT_FREEZE_PATH) : undefined}
          onSubmitEvidence={handleSubmitRowEvidence}
          focusFallbackRef={tabBarRef}
          shipmentCounts={shipmentCounts}
          // The ledger seam for the Freeze SKU drawer's recent-shipments list
          // (D17: last 50). Null in seeded mode: the harness has no ledger, and
          // the drawer leaves the section out rather than showing an empty one.
          fetchSkuHistory={live ? apiSkuHistory : null}
        />
      )}
      {tab === 'analytics' && (
        <RecoveryAnalytics rows={overviewRows} weeks={live ? serverWeeks : null} couriers={live ? serverCouriers : null} />
      )}

      <DisputeDrawer
        row={openRow}
        open={!!openRow}
        onClose={handleCloseDrawer}
        onAct={handleAct}
        onSubmitEvidence={handleSubmitRowEvidence}
        focusFallbackRef={tabBarRef}
      />
      {disputeActions.confirmDialog}

      <EvidenceStudio
        recordIds={evidenceStudioIds}
        rows={disputes}
        open={evidenceStudioOpen}
        onClose={handleCloseEvidenceStudio}
        onSubmit={handleSubmitEvidence}
      />
      </>
      )}
    </Box>
  )
}

// Also exported RAW (un-withLayout'd) for the dev-only harness at
// src/app/components/weight/__harness__/WeightHarness.js, which renders these
// pages without the app shell so layout can be checked without a login.
// withLayout pulls in Sidebar/TopBar and the authenticated merchant context.
export {WeightDisputesV2}

export default withLayout(WeightDisputesV2)
